import {useEffect} from 'react';

import {useDisclosure} from '../../hooks/useDisclosure';
import {DiscardChangesModal} from '../DiscardChangesModal';

interface UseDiscardGuardParams {
  isOpen: boolean | undefined;
  isDirty: boolean;
  onOpenChange?: (open: boolean) => void;
  discardDescription?: string;
}

/** Holds a modal's close behind the discard confirmation while `isDirty`; otherwise closes straight away. */
export function useDiscardGuard({
  isOpen,
  isDirty,
  onOpenChange,
  discardDescription
}: UseDiscardGuardParams) {
  const {isOpen: isConfirmOpen, open: openConfirm, close: closeConfirm} = useDisclosure();

  // The modal stays mounted when its parent closes it directly, so a pending confirm must not survive into the next open.
  useEffect(() => {
    if (!isOpen) closeConfirm();
  }, [isOpen, closeConfirm]);

  const requestClose = () => {
    if (!isDirty) return onOpenChange?.(false);
    openConfirm();
  };

  return {
    requestClose,
    handleOpenChange: (open: boolean) => (open ? onOpenChange?.(true) : requestClose()),
    discardModal: (
      <DiscardChangesModal
        isOpen={isConfirmOpen}
        onKeepEditing={closeConfirm}
        onDiscard={() => {
          closeConfirm();
          onOpenChange?.(false);
        }}
        description={discardDescription}
      />
    )
  };
}
